import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import {
  PortcoGlobal,
  Surface,
  Header,
  HeaderInner,
  HeaderBrand,
  HeaderBrandMark,
  HeaderCrumb,
  Body,
  Hero,
  HeroEyebrow,
  HeroTitle,
  HeroSub,
  StatStrip,
  StatCell,
  StatLabel,
  StatValue,
  StatSub,
  Panel,
  PanelHead,
  PanelTitle,
  PanelMeta,
  PanelBody,
  Table,
  Thead,
  Th,
  Td,
  TdMono,
  Tr,
  Loading,
  Empty,
  portcoTokens,
} from '../portco/PortcoTheme';

/**
 * GP all-LPs roster — single pane that answers "who has committed to which
 * fund, how much is called/distributed/unfunded, and when did each LP last
 * see capital action."
 *
 * Backend: GET /api/gp/lp-roster (tenant-wide) and /api/gp/funds/{id}/lp-roster
 * (per-fund). Aggregates fact_commitment + last-activity from
 * fact_capital_call / fact_distribution per commitment.
 *
 * Embedded mode (`embedded={true}`) mirrors GPDashboardScreen — used inside
 * the Alternatives → Portfolio Companies workbench. Suppresses outer chrome
 * and skips global style injection so the dark Alts theme stays.
 */
interface LPRosterRow {
  portfolioId: number;
  lpName: string;
  fundId: number;
  fundName: string;
  commitmentDate?: string;
  currency?: string;
  commitmentAmount: number;
  calledAmount: number;
  distributedAmount: number;
  unfundedAmount: number;
  calledPct?: number;
  status?: string;
  hasSideLetter?: boolean;
  lastActivityDate?: string;
}

interface FundOption {
  id: number;
  fundName: string;
  fundType?: string;
  vintageYear?: number;
}

const fmtMoney = (v?: number) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

const fmtPct = (v?: number, decimals = 1) =>
  v == null ? '—' : `${v.toFixed(decimals)}%`;

const fmtDate = (iso?: string) => (iso ? iso.substring(0, 10) : '—');

const StatStrip5 = styled(StatStrip)`
  grid-template-columns: repeat(5, 1fr);
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const ToolbarLabel = styled.span`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  font-weight: 600;
`;

const ToolbarSelect = styled.select`
  font-family: inherit;
  font-size: 13px;
  background: ${portcoTokens.surfaceMuted};
  border: 1px solid ${portcoTokens.hairline};
  color: ${portcoTokens.inkBody};
  padding: 6px 10px;
  min-width: 220px;
  cursor: pointer;
  &:focus { outline: 2px solid ${portcoTokens.accent}; outline-offset: 1px; }
`;

const ToolbarSearch = styled.input`
  font-family: inherit;
  font-size: 13px;
  background: ${portcoTokens.surfaceMuted};
  border: 1px solid ${portcoTokens.hairline};
  color: ${portcoTokens.inkBody};
  padding: 6px 10px;
  min-width: 220px;
  &:focus { outline: 2px solid ${portcoTokens.accent}; outline-offset: 1px; }
`;

const StatusPill = styled.span<{ $status?: string }>`
  display: inline-block;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 8px;
  border: 1px solid ${portcoTokens.hairline};
  font-weight: 600;
  color: ${(p) =>
    p.$status === 'ACTIVE' ? portcoTokens.statusPassing
      : p.$status === 'CLOSED' || p.$status === 'WOUND_DOWN' ? portcoTokens.inkSubtle
      : portcoTokens.inkMuted};
  background: ${(p) =>
    p.$status === 'ACTIVE' ? portcoTokens.statusPassingSubtle
      : portcoTokens.surfaceMuted};
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const SideLetterDot = styled.span<{ $on: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.$on ? portcoTokens.statusWarning : 'transparent')};
  border: 1px solid ${(p) => (p.$on ? portcoTokens.statusWarning : portcoTokens.hairlineStrong)};
`;

interface GPLPRosterScreenProps {
  embedded?: boolean;
}

const GPLPRosterScreen: React.FC<GPLPRosterScreenProps> = ({ embedded = false }) => {
  const [rows, setRows] = useState<LPRosterRow[]>([]);
  const [funds, setFunds] = useState<FundOption[]>([]);
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fund picker — optional. Roster degrades to tenant-wide if endpoint absent.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get<FundOption[]>('/api/alternatives/funds');
        if (!cancelled) setFunds(Array.isArray(r.data) ? r.data : []);
      } catch {
        if (!cancelled) setFunds([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = selectedFundId
          ? `/api/gp/funds/${selectedFundId}/lp-roster`
          : '/api/gp/lp-roster';
        const r = await axios.get<LPRosterRow[]>(url);
        if (!cancelled) setRows(Array.isArray(r.data) ? r.data : []);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load LP roster.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedFundId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      (r.lpName || '').toLowerCase().includes(q) ||
      (r.fundName || '').toLowerCase().includes(q));
  }, [rows, search]);

  const totals = useMemo(() => {
    const t = {
      lps: new Set<number>(),
      commitments: filtered.length,
      commitment: 0,
      called: 0,
      distributed: 0,
      unfunded: 0,
    };
    filtered.forEach((r) => {
      t.lps.add(r.portfolioId);
      t.commitment += r.commitmentAmount || 0;
      t.called += r.calledAmount || 0;
      t.distributed += r.distributedAmount || 0;
      t.unfunded += r.unfundedAmount || 0;
    });
    return {
      uniqueLPs: t.lps.size,
      commitments: t.commitments,
      commitment: t.commitment,
      called: t.called,
      distributed: t.distributed,
      unfunded: t.unfunded,
      blendedCalledPct: t.commitment > 0 ? (t.called / t.commitment) * 100 : null,
    };
  }, [filtered]);

  const body = (
    <Body>
      <Hero>
        <HeroEyebrow>GP Workbench · Investor Relations</HeroEyebrow>
        <HeroTitle>LP Roster</HeroTitle>
        <HeroSub>
          Every limited-partner commitment across the firm: dollar-weighted called/distributed/unfunded
          balances and last capital event. Filter by fund or search by LP / fund name.
        </HeroSub>
      </Hero>

      <Toolbar>
        <ToolbarLabel>Fund</ToolbarLabel>
        <ToolbarSelect
          value={selectedFundId == null ? '' : String(selectedFundId)}
          onChange={(e) => setSelectedFundId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">All funds</option>
          {funds.map((f) => (
            <option key={f.id} value={f.id}>
              {f.fundName}{f.vintageYear ? ` · ${f.vintageYear}` : ''}
            </option>
          ))}
        </ToolbarSelect>
        <ToolbarLabel>Search</ToolbarLabel>
        <ToolbarSearch
          value={search}
          placeholder="LP or fund name"
          onChange={(e) => setSearch(e.target.value)}
        />
      </Toolbar>

      <StatStrip5>
        <StatCell>
          <StatLabel>Unique LPs</StatLabel>
          <StatValue>{totals.uniqueLPs.toLocaleString()}</StatValue>
          <StatSub>{totals.commitments.toLocaleString()} commitment rows</StatSub>
        </StatCell>
        <StatCell>
          <StatLabel>Total commitment</StatLabel>
          <StatValue>{fmtMoney(totals.commitment)}</StatValue>
          <StatSub>across all displayed rows</StatSub>
        </StatCell>
        <StatCell>
          <StatLabel>Called</StatLabel>
          <StatValue>{fmtMoney(totals.called)}</StatValue>
          <StatSub>blended {fmtPct(totals.blendedCalledPct ?? undefined)}</StatSub>
        </StatCell>
        <StatCell>
          <StatLabel>Distributed</StatLabel>
          <StatValue>{fmtMoney(totals.distributed)}</StatValue>
          <StatSub>cumulative to LPs</StatSub>
        </StatCell>
        <StatCell>
          <StatLabel>Unfunded</StatLabel>
          <StatValue>{fmtMoney(totals.unfunded)}</StatValue>
          <StatSub>capital available to call</StatSub>
        </StatCell>
      </StatStrip5>

      <Panel>
        <PanelHead>
          <PanelTitle>Commitments {selectedFundId ? '— selected fund' : '— firm-wide'}</PanelTitle>
          <PanelMeta>{filtered.length.toLocaleString()} rows</PanelMeta>
        </PanelHead>
        <PanelBody style={{ padding: 0 }}>
          {loading ? (
            <Loading>Loading LP roster…</Loading>
          ) : error ? (
            <Empty>{error}</Empty>
          ) : filtered.length === 0 ? (
            <Empty>No commitments match the current filters.</Empty>
          ) : (
            <TableScroll>
              <Table>
                <Thead>
                  <tr>
                    <Th>LP</Th>
                    <Th>Fund</Th>
                    <Th>Commitment date</Th>
                    <Th style={{ textAlign: 'right' }}>Commitment</Th>
                    <Th style={{ textAlign: 'right' }}>Called</Th>
                    <Th style={{ textAlign: 'right' }}>Distributed</Th>
                    <Th style={{ textAlign: 'right' }}>Unfunded</Th>
                    <Th style={{ textAlign: 'right' }}>% Called</Th>
                    <Th>Status</Th>
                    <Th>Side letter</Th>
                    <Th>Last activity</Th>
                  </tr>
                </Thead>
                <tbody>
                  {filtered.map((r) => (
                    <Tr key={`${r.portfolioId}-${r.fundId}`}>
                      <Td>{r.lpName || `LP #${r.portfolioId}`}</Td>
                      <Td>{r.fundName || `Fund #${r.fundId}`}</Td>
                      <TdMono>{fmtDate(r.commitmentDate)}</TdMono>
                      <TdMono style={{ textAlign: 'right' }}>{fmtMoney(r.commitmentAmount)}</TdMono>
                      <TdMono style={{ textAlign: 'right' }}>{fmtMoney(r.calledAmount)}</TdMono>
                      <TdMono style={{ textAlign: 'right' }}>{fmtMoney(r.distributedAmount)}</TdMono>
                      <TdMono style={{ textAlign: 'right' }}>{fmtMoney(r.unfundedAmount)}</TdMono>
                      <TdMono style={{ textAlign: 'right' }}>{fmtPct(r.calledPct)}</TdMono>
                      <Td><StatusPill $status={r.status}>{r.status || '—'}</StatusPill></Td>
                      <Td><SideLetterDot $on={!!r.hasSideLetter} /></Td>
                      <TdMono>{fmtDate(r.lastActivityDate)}</TdMono>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          )}
        </PanelBody>
      </Panel>
    </Body>
  );

  if (embedded) {
    return <div className="portco-embedded">{body}</div>;
  }

  return (
    <>
      <PortcoGlobal />
      <div className="portco-surface">
        <Surface>
          <Header>
            <HeaderInner>
              <HeaderBrand>
                <HeaderBrandMark>AX</HeaderBrandMark>
                AxiomFlow · GP Workbench
              </HeaderBrand>
              <HeaderCrumb>Alternatives › LP Roster</HeaderCrumb>
            </HeaderInner>
          </Header>
          {body}
        </Surface>
      </div>
    </>
  );
};

export default GPLPRosterScreen;
